import React from 'react';
import clsx from 'clsx';
import * as Headless from '@headlessui/react';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';

import { FieldReferenceInput } from '~/components/email-templates/field-reference-input';

/**
 * Standard back-arrow close button used by every artifact focus screen.
 */
export const ArtifactFocusCloseButton = (props: { onClose: () => void; label?: string }) => (
  <div className="flex flex-row items-center gap-2">
    <Headless.Button
      className="rounded-full -ml-3 py-1.5 pl-2 pr-3 flex items-center justify-center gap-2 hover:bg-neutral-200/65 dark:hover:bg-white/10 text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white transition-all duration-150 cursor-pointer"
      onClick={props.onClose}
    >
      <ChevronLeftIcon className="size-4" />
      <div className="text-xs">{props.label ?? 'Close viewer'}</div>
    </Headless.Button>
  </div>
);

/**
 * Iframe styled for the artifact focus content area. Used by every iframe-based
 * focus (Pdf, CustomForm, TemplateForm).
 */
export const ArtifactIframe = (props: { src: string; title: string }) => (
  <iframe
    src={props.src}
    title={props.title}
    className="relative mt-2 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-y-auto flex flex-col flex-grow"
  />
);

const TITLE_INPUT_CLASS =
  'mt-0 w-full border-none p-0 text-lg font-medium placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:outline-none focus:ring-0';

/**
 * Standard chrome for an artifact focus screen: back button, heading + version,
 * optional header actions, and a children slot for the body.
 */
export const ArtifactFocusShell = (props: {
  title: string;
  /** May be formatted so a revision reads as 2.1 rather than being rounded to 2. */
  version: string | number;
  /**
   * Sits beside the title in place of the "Version N" line under it, for a
   * screen that moves between versions.
   */
  versionControl?: React.ReactNode;
  onClose: () => void;
  closeLabel?: string;
  headerActions?: React.ReactNode;
  /**
   * Status that rides the close button's row, which is otherwise empty. A
   * screen whose state is one settled fact puts it here rather than spending a
   * band of the rail on it above the fields.
   */
  aside?: React.ReactNode;
  /**
   * Makes the heading the field that edits the title. Given, the heading is the
   * only place the title is written, so a screen must not also carry a row for
   * it.
   */
  onTitleChange?: (title: string) => void;
  /** Committed on blur, since `onTitleChange` fires on every keystroke. */
  onTitleCommit?: () => void;
  titleLabel?: string;
  /** Shown in the heading while the title is empty, so the field does not vanish into the rail. */
  titlePlaceholder?: string;
  titleDisabled?: boolean;
  /** Shows each [[field]] in the editable title as a chip, as an email's subject does. */
  titleHasFieldReferences?: boolean;
  children: React.ReactNode;
}) => (
  <div className="h-full flex flex-col">
    <div className="flex items-center justify-between gap-4">
      <ArtifactFocusCloseButton onClose={props.onClose} label={props.closeLabel} />
      {props.aside}
    </div>
    <div
      className={clsx(
        'mt-2 flex flex-row justify-between gap-4',
        props.versionControl ? 'items-center' : 'items-start'
      )}
    >
      <div
        className={clsx(
          'min-w-0 flex-1',
          props.versionControl && 'flex flex-wrap items-center gap-x-3 gap-y-1'
        )}
      >
        {props.onTitleChange && props.titleHasFieldReferences ? (
          <FieldReferenceInput
            value={props.title}
            onChange={(e) => props.onTitleChange?.(e.target.value)}
            onBlur={props.onTitleCommit}
            aria-label={props.titleLabel ?? 'Title'}
            placeholder={props.titlePlaceholder}
            disabled={props.titleDisabled}
            className={TITLE_INPUT_CLASS}
          />
        ) : props.onTitleChange ? (
          <Headless.Input
            value={props.title}
            onChange={(e) => props.onTitleChange?.(e.target.value)}
            onBlur={props.onTitleCommit}
            aria-label={props.titleLabel ?? 'Title'}
            placeholder={props.titlePlaceholder}
            disabled={props.titleDisabled}
            className={clsx(TITLE_INPUT_CLASS, 'bg-transparent text-neutral-900 dark:text-white')}
          />
        ) : (
          <h1 className="mt-0 group text-lg font-medium text-neutral-900 dark:text-white">
            {props.title}
          </h1>
        )}
        {props.versionControl ? (
          <div className="shrink-0">{props.versionControl}</div>
        ) : (
          <p className="text-xs mt-1 text-neutral-400 dark:text-neutral-500">
            Version {props.version}
          </p>
        )}
      </div>
      {props.headerActions}
    </div>
    {props.children}
  </div>
);
